import {signal, computed} from "@preact/signals-react"

export const isLoadingBatches = signal(true)
export const batchesTableData = signal([])
export const batchStatusFilter = signal('active') // 'active', 'finished', or 'all'
export const batchOriginFilter = signal('all') // 'all', 'manual', or 'recurrent'
// 'all' or an id_customer. Applied SERVER-SIDE, like the origin filter: the customer
// lives inside the batch config, not in a column, so the table cannot filter on it.
export const batchCustomerFilter = signal('all')
export const batchCustomerOptions = signal([]) // [{value, label}] for the toolbar select
export const activeBatchTab = signal('sku2raw') // a batch type, or 'schedules'
export const selectedBatchIds = signal([]) // Selected batch IDs for bulk actions
export const batchesTableState = signal({
    sortOrder: { name: 'createdAt', direction: 'desc' },
    page: 0,
    rowsPerPage: 10,
    searchText: ''
})

// Computed: filtered batches based on active tab
export const filteredBatchesTableData = computed(() => {
    const data = batchesTableData.value
    const activeTab = activeBatchTab.value
    return data.filter(batch => (batch.batchType || 'sku2raw') === activeTab)
})

// Computed: summary metrics for filtered batches (only 'ready' status batches)
export const batchesSummaryMetrics = computed(() => {
    const batches = filteredBatchesTableData.value
    const readyBatches = batches.filter(b => b.effectiveStatus === 'ready' || b.status === 'ready')

    const totalPendingTasks = readyBatches.reduce((sum, b) => sum + (b.pendingTasks || 0), 0)
    const totalPendingQa = readyBatches.reduce((sum, b) => sum + (b.pendingQa || 0), 0)

    const assignedBatches = readyBatches.filter(b => b.userID != null)
    const unassignedBatches = readyBatches.filter(b => b.userID == null)

    const assignedTasks = assignedBatches.reduce((sum, b) => sum + (b.pendingTasks || 0), 0)
    const unassignedTasks = unassignedBatches.reduce((sum, b) => sum + (b.pendingTasks || 0), 0)

    const distinctUsers = [...new Set(assignedBatches.map(b => b.userID).filter(Boolean))]

    return {
        totalBatches: readyBatches.length,
        totalPendingTasks,
        totalPendingQa,
        assignedBatches: assignedBatches.length,
        unassignedBatches: unassignedBatches.length,
        assignedTasks,
        unassignedTasks,
        distinctUsersCount: distinctUsers.length
    }
})
export const selectedBatchForDetails = signal(null)
export const selectedBatchForProducts = signal(null)
export const batchDetailsCache = signal({})
export const isLoadingBatchDetailsById = signal({})
export const batchDetailsErrorById = signal({})

// Create Batch Form
export const isCreateBatchDrawerOpen = signal(false)
export const isCreatingBatch = signal(false)
export const showCreatingBatchModal = signal(false)
export const createBatchFormData = signal({
    batchType: 'sku2raw', // see @constants/batchTypes
    batchName: '',
    ownSites: [],         // For war2war batches - own sites holding the candidate catalog
    firstDateDays: 30,    // For war2war batches - how new a competitor listing has to be
    markets: [],     // For raw2sku batches - multiple markets alternative to sites
    sites: [],
    customerID: null,
    brands: [],
    categories: [], // Category filter (raw2sku/sku2raw) - taxonomy string keys
    skus: [],
    dateStart: null, // For raw2sku batches
    dateEnd: null,   // For raw2sku batches
    dateField: 'end_date', // For raw2sku batches: 'end_date' (last seen) or 'first_date' (first seen)
    splitByBrands: false, // For raw2sku batches - create one batch per brand
    splitBySites: false,  // For raw2sku batches - create one batch per site
    topVentas: false,     // For raw2raw batches - only top-sales own products
    tags: [],             // For raw2raw batches - only own products with these tags
    retryNotFound: false, // Any type - retry every not-found, ignoring the new-candidate check
    splitByTargetSites: true, // For raw2raw batches - one batch per competitor site (default on)
    groupBy: null, // null | 'reference' | 'product_url'
    // Batch-level auto-validation thresholds (null = use system defaults)
    autoValidation: {
        minRateAutovalidate: null,
        minGapSecondCandidate: null,
        maxRateAutoexclude: null
    },
    // raw2raw: AI second pass launched automatically when the batch finishes
    // (benchmark-validated defaults: Luna @0.8, not-found @0.9, 5% QA sample)
    autoSecondPass: {
        enabled: false,
        model: 'gpt-5.6-luna',
        threshold: null,    // null = calibrated default for the batch type
        nfThreshold: null,  // (raw2raw/sku2raw: 0.8/0.9 · raw2sku: 0.9/0.95)
        samplePct: 0,
        speedMode: false    // OFF = OpenAI Batch API (cheap, up to 24h) · ON = sync
    }
})

// Autovalidate
export const selectedBatchForAutovalidate = signal(null)
export const autovalidateParams = signal({
    minRateAutovalidate: 0.95,
    minGapSecondCandidate: 0.05,
    maxRateAutoexclude: 0.80
})
export const autovalidatePreview = signal(null)
export const isLoadingAutovalidatePreview = signal(false)

// Batch Items (all items detail view)
export const selectedBatchForItems = signal(null) // {batchID, batchType, batchName}
export const batchItemsData = signal([])
export const batchItemsPagination = signal({
    page: 1,
    pageSize: 50,
    totalItems: 0,
    totalPages: 0
})
export const batchItemsMethodFilter = signal('all') // 'all', 'auto', 'manual'
export const batchItemsResultFilter = signal('all') // 'all', 'mapped', 'excluded', 'pending'
export const batchItemsSearch = signal('') // free-text, comma = AND across product/sku/site
export const isLoadingBatchItems = signal(false)
export const batchItemsSummary = signal(null) // {total, pending, autoMappedEan, autoMappedIa, autoExcluded, manual}

// Edit mapping drawer (from BatchItemsTable)
export const batchItemEditDrawer = signal(null) // { item: rowData, batchType: 'sku2raw'|'raw2sku' }

// Recheck mapping dialog (from BatchItemsTable)
export const batchItemRecheckDialog = signal(null) // { item: rowData, batchType: 'sku2raw'|'raw2sku' }
